import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useAuthStore } from "@/store/authStore";

/**
 * Phase 1 Users API (backend.md §24.3).
 *
 * The /users router is scoped server-side from PermissionScope + the
 * X-Acting-As-Client header. Non-SuperAdmin callers see only their home
 * client; SuperAdmin sees everyone unless the header / client_id filter
 * narrows. No client-side scope enforcement beyond the RoleGate wrapper.
 *
 * Every tenant-scoped queryKey carries `actingAs.clientId` as a key
 * segment (CROSS_PAGE_DECISIONS §13) so the React Query cache stays
 * partitioned per impersonation context — flipping tenants never reads
 * the previous tenant's user list out of cache. Mutations don't include
 * the suffix (invalidate-by-prefix nukes every variant).
 */

export type CognitoGroup = "SuperAdmin" | "ClientAdmin" | "User";

/**
 * MV2-178 + MV2-243: compact policy summary embedded on UserListRow
 * rows so the Users table can render policy chips without a per-row
 * drill-in. Full policy detail lives at /policies/{id}. ``site_scope``
 * dropped — policies no longer carry site fields.
 */
export interface PolicySummary {
  id: string;
  name: string;
  /**
   * Slice 6 (MV2-899): owner + feature list ride along so the user record
   * can say "Field engineer · Verde custom · 14 features" and derive the
   * persona without a second request. Optional until backend PR #494 is
   * deployed everywhere.
   */
  is_global?: boolean;
  features?: string[];
}

/**
 * Slice 6 (MV2-899): the persona a user row produces — `cognito_group` for
 * admins; for `User` rows `OM` when any attached policy carries an O&M
 * signature feature, else `SiteOwner`. Same rule as `/me.primary_persona`.
 */
export type PersonaClass = "SuperAdmin" | "ClientAdmin" | "OM" | "SiteOwner";

/**
 * MV2-243: home-client site visibility mode for a User-role account.
 *
 *   * ``all``       — see every site under the home client
 *   * ``customer``  — narrow to a single customer's sites (auto-grows)
 *   * ``specific``  — narrow to a hand-picked site list
 *
 * ClientAdmin always sees the whole client; the field is stored on
 * invite/edit for audit but doesn't change ClientAdmin's visibility.
 */
export type UserScopeMode = "all" | "customer" | "specific";

export interface UserResponse {
  id: string;
  email: string;
  full_name: string;
  client_id: string;
  customer_id: string | null;
  cognito_group: CognitoGroup;
  status: string;
  avatar_s3_key: string | null;
  phone: string | null;
  default_timezone: string | null;
  locale: string;
  email_verified: boolean;
  last_login_at: string | null;
  first_login_at: string | null;
  created_at: string;
  updated_at: string;
  deactivated_at: string | null;
  /**
   * MV2-157: backend's `UserListRow` extension on `/users` and
   * `/users/search` — the list endpoints LEFT JOIN ``clients`` and
   * surface the name so the User Management table can render readable
   * scope labels instead of UUID prefixes. Single-user detail
   * (`GET /users/{id}`) does not populate these — keep optional so the
   * detail page consumers don't need to defend against undefined.
   */
  client_name?: string;
  customer_name?: string | null;
  /**
   * MV2-178: attached active policies (sorted by name server-side).
   * Optional because `GET /users/{id}` (single-user detail) doesn't
   * populate the field; only the list / search endpoints do.
   */
  policies?: PolicySummary[];
  /**
   * Slice 6 (MV2-899, backend.md §53.4) — the read side of what
   * `PATCH /users/{id}/scope` writes, plus persona + invite provenance.
   * `scope_mode` is derived server-side (customer_id → customer;
   * user_site_assignments → specific; neither → all). `site_ids` is
   * populated whenever assignment rows exist — including customer mode
   * narrowed to a subset — so the scope editor round-trips hand-picked
   * sites instead of wiping them. `site_names` is the first three,
   * name-sorted. All optional: older backends omit them and the UI
   * derives what it can (`pages/admin/users/userFormat.ts`).
   */
  scope_mode?: UserScopeMode;
  site_count?: number;
  site_ids?: string[];
  site_names?: string[];
  persona_class?: PersonaClass;
  invited_by_name?: string | null;
  invitation_sent_at?: string | null;
  invitation_expires_at?: string | null;
}

export interface UserInviteCreate {
  email: string;
  full_name: string;
  cognito_group: CognitoGroup;
  /**
   * Only valid when ``scope_mode === 'customer'``. The Pydantic model
   * on the backend enforces the mutually-exclusive shape.
   */
  customer_id?: string | null;
  policy_ids?: string[];
  /**
   * MV2-243: home-client site visibility mode. Defaults to ``'all'``
   * server-side when omitted.
   */
  scope_mode?: UserScopeMode;
  /**
   * Only valid when ``scope_mode === 'specific'`` — the hand-picked
   * site list lands as ``user_site_assignments`` rows.
   */
  site_ids?: string[];
  default_timezone?: string | null;
  locale?: string | null;
}

/**
 * MV2-243: body for ``PATCH /users/{id}/scope`` — re-scopes an
 * existing user without touching their attached policies. Same
 * mutually-exclusive shape as the invite ``scope_mode`` triple.
 */
export interface UserScopeUpdate {
  scope_mode: UserScopeMode;
  customer_id?: string | null;
  site_ids?: string[];
}

export interface UserInviteResponse {
  user_id: string;
  invitation_id: string;
  invitation_token: string;
  already_existed: boolean;
}

export interface UserUpdate {
  full_name?: string;
  phone?: string | null;
  default_timezone?: string | null;
  locale?: string | null;
  customer_id?: string | null;
}

interface UserListParams {
  client_id?: string;
  customer_id?: string;
  /**
   * MV2-175: opt into deactivated users in the response. Default false
   * keeps legacy behaviour (only active rows). The User Management
   * table flips this on whenever `statusFilter === 'deactivated'` is
   * picked so the deactivated rows actually show up.
   */
  include_inactive?: boolean;
}

export function useUsers(params: UserListParams = {}) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  const includeInactive = params.include_inactive ?? false;
  return useQuery<UserResponse[]>({
    queryKey: [
      "users",
      params.client_id ?? null,
      params.customer_id ?? null,
      includeInactive,
      actingAsClientId,
    ],
    queryFn: async () => {
      const query: Record<string, string | boolean> = {};
      if (params.client_id) query.client_id = params.client_id;
      if (params.customer_id) query.customer_id = params.customer_id;
      if (includeInactive) query.include_inactive = true;
      return (await apiClient.get<UserResponse[]>("/users", { params: query }))
        .data;
    },
    staleTime: 30_000,
  });
}

/**
 * MV2-1250 — the users a SuperAdmin can "View as": the Acting-as client's
 * active users minus SuperAdmins (backend.md §108 refuses those). Sent
 * as yourself (`skipViewAs`) so the list survives while viewing as
 * someone, and you can switch straight to another user.
 */
export function useViewAsCandidates(enabled: boolean) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<UserResponse[]>({
    queryKey: ["users", "view-as", actingAsClientId],
    queryFn: async () => {
      const rows = (
        await apiClient.get<UserResponse[]>("/users", {
          params: { client_id: actingAsClientId },
          skipViewAs: true,
        })
      ).data;
      return rows
        .filter((u) => u.cognito_group !== "SuperAdmin")
        .sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
    enabled: enabled && actingAsClientId !== null,
    staleTime: 5 * 60_000,
  });
}

/**
 * MV2-1254 — ⌘K "View as <name>": a typeahead over every client's users,
 * sent as yourself with no Acting as (`skipActingAs` + `skipViewAs`), so a
 * SuperAdmin finds anyone from anywhere. SuperAdmins are dropped (§108
 * refuses them). Unscoped by design, so no `actingAs.clientId` in the key.
 */
export function useViewAsSearch(q: string, enabled: boolean) {
  const needle = q.trim();
  return useQuery<UserResponse[]>({
    queryKey: ["users", "view-as-search", needle],
    queryFn: async () =>
      (
        await apiClient.get<UserResponse[]>("/users/search", {
          params: { q: needle, limit: 8 },
          skipActingAs: true,
          skipViewAs: true,
        })
      ).data.filter((u) => u.cognito_group !== "SuperAdmin"),
    enabled: enabled && needle.length >= 2,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** GET /users/search — typeahead-scoped lookup for assignee pickers
 * and @-mention autocomplete.  No admin guard on the backend; server
 * enforces persona scope (home client / acting-as for SuperAdmin).
 * Passing an empty `q` returns the first N alphabetical users —
 * useful for priming the dropdown on focus.
 *
 * `clientId` (optional) targets a specific tenant.  When set, the
 * server returns same-client + cross-client-grant users for that
 * tenant — used by the ticket-detail @-mention typeahead so
 * contractors / external engineers with grants are mention-able even
 * when the caller's home client differs from the ticket's.  Server
 * silently falls back to caller scope if the caller has no access to
 * the requested target client.
 *
 * `siteId` (optional, MV2-269) further narrows to users who can
 * actually act on a specific site — i.e. valid ticket assignees.
 * Includes: same-client SuperAdmin/ClientAdmin (wildcard), same-client
 * User-role with an explicit `user_site_assignments` row for the site,
 * and cross-client-grant peers whose grant covers the site. Site
 * implies client, so passing `siteId` makes `clientId` redundant —
 * the server uses the site's owning client as the effective tenant.
 * Use for the Assignee picker; leave OFF for @-mention autocomplete
 * (mentions are intentionally client-wide so operators can ping
 * teammates across sites). */
export function useUsersSearch(
  q: string,
  options: { enabled?: boolean; clientId?: string | null; siteId?: string | null } = {},
) {
  const clientId = options.clientId ?? null;
  const siteId = options.siteId ?? null;
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<UserResponse[]>({
    queryKey: ["users", "search", clientId, siteId, q, actingAsClientId],
    queryFn: async () => {
      const params: Record<string, string | number> = { q, limit: 20 };
      if (clientId) params.client_id = clientId;
      if (siteId) params.site_id = siteId;
      return (await apiClient.get<UserResponse[]>("/users/search", { params }))
        .data;
    },
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

/**
 * `GET /users/{id}` — one user record (Slice 6: returns the hydrated
 * `UserListRow` shape, so `client_name`, `policies`, `scope_mode`… are
 * present on a deep link with no list in cache). `includeInactive` adds
 * `?include_inactive=true` so a deactivated user's record still loads —
 * Reactivate lives on that record (backend.md §53.4). Default stays the
 * 404-on-deactivated behaviour for callers that want it.
 */
export function useUser(
  userId: string | undefined,
  options: { includeInactive?: boolean } = {},
) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  const includeInactive = options.includeInactive ?? false;
  return useQuery<UserResponse>({
    queryKey: ["user", userId, includeInactive, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<UserResponse>(`/users/${userId}`, {
          params: includeInactive ? { include_inactive: true } : undefined,
        })
      ).data,
    enabled: Boolean(userId),
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation<UserInviteResponse, Error, UserInviteCreate>({
    mutationFn: async (body) =>
      (await apiClient.post<UserInviteResponse>("/users/invite", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}

export function useUpdateUser(userId: string) {
  const qc = useQueryClient();
  return useMutation<UserResponse, Error, UserUpdate>({
    mutationFn: async (body) =>
      (await apiClient.patch<UserResponse>(`/users/${userId}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
}

/**
 * MV2-243: PATCH /users/{id}/scope — re-scope a user without
 * touching their attached policies. Wipes user_site_assignments +
 * resets customer_id atomically based on the new ``scope_mode``.
 */
export function useUpdateUserScope(userId: string) {
  const qc = useQueryClient();
  return useMutation<UserResponse, Error, UserScopeUpdate>({
    mutationFn: async (body) =>
      (await apiClient.patch<UserResponse>(`/users/${userId}/scope`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user", userId] });
      // /me may surface a stale site list for the caller if they
      // re-scoped themselves; cheap refetch to repair.
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation<UserResponse, Error, { userId: string; reason?: string }>({
    mutationFn: async ({ userId, reason }) =>
      (
        await apiClient.post<UserResponse>(`/users/${userId}/deactivate`, {
          reason: reason ?? null,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation<UserResponse, Error, string>({
    mutationFn: async (userId) =>
      (await apiClient.post<UserResponse>(`/users/${userId}/reactivate`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

/**
 * MV2-176: admin-initiated password reset.
 *
 * Calls `POST /users/{id}/admin-reset-password` (ClientAdmin / SuperAdmin
 * gated, tenant-scoped server-side). Backend puts the user into Cognito's
 * ResetRequired state — they'll set a new password on next login — and
 * dispatches a branded SES notification. Returns the updated UserResponse
 * so the caller can refresh chrome (e.g. updated_at, status) without an
 * extra round-trip.
 *
 * Use case: admin rescuing a user who's lost their password / can't run
 * forgot-password themselves (broken email, locked account, …). For the
 * common self-service path the user-initiated forgot-password flow on
 * the login page is still the right tool.
 */
export function useAdminResetUserPassword() {
  const qc = useQueryClient();
  return useMutation<UserResponse, Error, string>({
    mutationFn: async (userId) =>
      (
        await apiClient.post<UserResponse>(
          `/users/${userId}/admin-reset-password`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

/**
 * Re-dispatch the home-client invitation email for a pending user
 * (P3.5 B4). Operator-triggered recovery for silently-failed SES
 * dispatches, expired links, or recipient-deleted-the-email cases.
 *
 * Same token stays on the row if the existing expiry is still valid
 * (or NULL = never expires); rotates only on explicit past-expiry.
 * Any link already shared to Slack/SMS keeps working after the call.
 */
export function useResendUserInvite() {
  const qc = useQueryClient();
  return useMutation<UserInviteResponse, Error, string>({
    mutationFn: async (userId) =>
      (
        await apiClient.post<UserInviteResponse>(
          `/users/${userId}/resend-invite`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

/**
 * Slice 6 (MV2-899) — the Users list's bulk bar. There is no bulk
 * endpoint; each of these fans out over the selection with the same
 * per-user calls the row actions make (`Promise.allSettled`, so one
 * failure doesn't hide the rest). The result reports which ids failed so
 * the bar can say "2 of 3 resent".
 */
export interface BulkResult {
  ok: string[];
  failed: { userId: string; error: string }[];
}

async function fanOut(
  userIds: string[],
  call: (userId: string) => Promise<unknown>,
): Promise<BulkResult> {
  const settled = await Promise.allSettled(userIds.map((id) => call(id)));
  const out: BulkResult = { ok: [], failed: [] };
  settled.forEach((r, i) => {
    const userId = userIds[i]!;
    if (r.status === "fulfilled") out.ok.push(userId);
    else
      out.failed.push({
        userId,
        error: r.reason instanceof Error ? r.reason.message : String(r.reason),
      });
  });
  return out;
}

export function useResendUserInvites() {
  const qc = useQueryClient();
  return useMutation<BulkResult, Error, { userIds: string[] }>({
    mutationFn: ({ userIds }) =>
      fanOut(userIds, (id) => apiClient.post(`/users/${id}/resend-invite`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

export function useDeactivateUsers() {
  const qc = useQueryClient();
  return useMutation<
    BulkResult,
    Error,
    { userIds: string[]; reason: string }
  >({
    mutationFn: ({ userIds, reason }) =>
      fanOut(userIds, (id) =>
        apiClient.post(`/users/${id}/deactivate`, { reason }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

export function useAttachPolicyToUsers() {
  const qc = useQueryClient();
  return useMutation<
    BulkResult,
    Error,
    { userIds: string[]; policyId: string }
  >({
    mutationFn: ({ userIds, policyId }) =>
      fanOut(userIds, (id) =>
        apiClient.post(`/policies/${policyId}/users/${id}`),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

/**
 * Users attached to a given policy. Drives the "Attached users" surface on
 * the policy detail page. Backend filters global policies down to the
 * caller's own client when the caller isn't SuperAdmin (backend.md §24.8).
 */
export function usePolicyUsers(policyId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<UserResponse[]>({
    queryKey: ["policy", policyId, "users", actingAsClientId],
    queryFn: async () =>
      (await apiClient.get<UserResponse[]>(`/policies/${policyId}/users`)).data,
    enabled: Boolean(policyId),
    staleTime: 15_000,
  });
}

// MV2-199: attach/detach users on the policy detail page. Backend
// endpoints are POST/DELETE /policies/{policyId}/users/{userId}.
// Both are idempotent. Refetch the attached-users list on success so
// the UI stays in sync.
export function useAttachUserToPolicy(policyId: string | undefined) {
  const qc = useQueryClient();
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useMutation<UserResponse, Error, { userId: string }>({
    mutationFn: async ({ userId }) =>
      (
        await apiClient.post<UserResponse>(
          `/policies/${policyId}/users/${userId}`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["policy", policyId, "users", actingAsClientId],
      });
    },
  });
}

/**
 * MV2-243: same backend endpoint as ``useAttachUserToPolicy`` but with
 * the closure shape inverted for the user-detail drawer — there the
 * user_id is fixed and the policy_id is what the operator picks. The
 * policy detail page kept the original shape because it's the user
 * list that varies.
 */
export function useAttachPolicyToUser(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<UserResponse, Error, { policyId: string }>({
    mutationFn: async ({ policyId }) =>
      (
        await apiClient.post<UserResponse>(
          `/policies/${policyId}/users/${userId}`,
        )
      ).data,
    onSuccess: () => {
      // The user list carries each user's attached-policy chips; refetch
      // so the new attachment shows up without a manual reload.
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
}

/**
 * MV2-243: detach by (user, policy) tuple from the user-detail drawer.
 * Same idempotent backend endpoint as ``useDetachUserFromPolicy`` — the
 * user-detail caller wants to bind the user_id, not the policy_id.
 */
export function useDetachPolicyFromUser(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, { policyId: string }>({
    mutationFn: async ({ policyId }) => {
      await apiClient.delete(`/policies/${policyId}/users/${userId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
}

export function useDetachUserFromPolicy(policyId: string | undefined) {
  const qc = useQueryClient();
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useMutation<void, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      await apiClient.delete(`/policies/${policyId}/users/${userId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["policy", policyId, "users", actingAsClientId],
      });
    },
  });
}
